import { Accordion, Box, Divider, TextLink, Typography, useMediaQueryContext } from "@drone-js/core";
import type { FunctionComponent } from "react";
import { PartnerMarketingLayout } from "./PartnerMarketingLayout";
import { usePartnerMarketingModelValue } from "./partnerMarketingModelAtom";

export const Faqs: FunctionComponent = () => {
    const {
        faqsSectionViewModel: {
            faqsSectionTitle,
            whatPropertyTypesCanBeListed,
            whatSolutionsDoesAgodaProvide,
            howDoIGetPaid,
            howDoISignMyContract,
            howMuchDoesAgodaCharge,
        },
    } = usePartnerMarketingModelValue();

    const mediaQuery = useMediaQueryContext();

    return (
        <PartnerMarketingLayout col={8} data-element-name="faqs-section">
            <Typography variant="heading-2xl" textAlign="center" mt={mediaQuery.m ? "64" : "48"} mb="32">
                {faqsSectionTitle}
            </Typography>
            <Accordion
                actionType="chevron-icon"
                actionPosition="end-below"
                view="collapsed"
                contentSummary={
                    <Typography
                        variant="body-default"
                        py="16"
                        textAlign="start"
                        data-element-name="what-property-types-question"
                    >
                        {whatPropertyTypesCanBeListed.question}
                    </Typography>
                }
                backgroundColor="base.transparent"
                borderRadius="4"
                level={2}
                data-element-name="faq"
                data-faq-element={0}
            >
                <Box rowGap="16" display="flex" flexWrap="wrap" data-element-name="what-property-types-answer">
                    {whatPropertyTypesCanBeListed.answers.map((answer, index) => (
                        <Typography key={index} variant="body-s" textColor="base.subtle" textAlign="start">
                            {answer.answer}
                        </Typography>
                    ))}
                </Box>
            </Accordion>
            <Divider direction="horizontal" />
            <Accordion
                actionType="chevron-icon"
                actionPosition="end-below"
                view="collapsed"
                contentSummary={
                    <Typography variant="body-default" py="16" textAlign="start">
                        {howDoIGetPaid.question}
                    </Typography>
                }
                backgroundColor="base.transparent"
                borderRadius="4"
                level={2}
                data-element-name="faq"
                data-faq-element={1}
            >
                <Box display="flex" flexWrap="wrap">
                    <Box pb="16">
                        <Typography variant="body-s" textColor="base.subtle" textAlign="start">
                            {howDoIGetPaid.agodaOffersMultiplePaymentOptions}
                        </Typography>
                    </Box>
                    <ul style={{ marginBottom: 0, marginTop: 0 }}>
                        {howDoIGetPaid.bulletPoints.map((point, index) => (
                            <li key={index}>
                                <Box pb="16">
                                    <Typography variant="body-s-strong" textColor="base.subtle" display="inline">
                                        {point.boldedText}
                                    </Typography>
                                    <Typography variant="body-s" textColor="base.subtle" display="inline">
                                        {point.bulletPoint}
                                    </Typography>
                                </Box>
                            </li>
                        ))}
                    </ul>
                    <Typography variant="body-s" textColor="base.subtle" textAlign="start" display="inline">
                        {howDoIGetPaid.moreDetails}
                        <TextLink
                            label={howDoIGetPaid.clickHere}
                            href={howDoIGetPaid.link}
                            target="_blank"
                            rel="noopener"
                            hierarchy="primary"
                        />
                    </Typography>
                </Box>
            </Accordion>
            <Divider direction="horizontal" />
            <Accordion
                actionType="chevron-icon"
                actionPosition="end-below"
                view="collapsed"
                contentSummary={
                    <Typography variant="body-default" py="16" textAlign="start">
                        {howDoISignMyContract.question}
                    </Typography>
                }
                backgroundColor="base.transparent"
                borderRadius="4"
                level={2}
                data-element-name="faq"
                data-faq-element={2}
            >
                <Typography variant="body-s" textColor="base.subtle" textAlign="start">
                    {howDoISignMyContract.answer}
                </Typography>
            </Accordion>
            <Divider direction="horizontal" />
            <Accordion
                actionType="chevron-icon"
                actionPosition="end-below"
                view="collapsed"
                contentSummary={
                    <Typography variant="body-default" py="16" textAlign="start">
                        {howMuchDoesAgodaCharge.question}
                    </Typography>
                }
                backgroundColor="base.transparent"
                borderRadius="4"
                level={2}
                data-element-name="faq"
                data-faq-element={3}
            >
                <Box rowGap="16" display="flex" flexWrap="wrap">
                    {howMuchDoesAgodaCharge.answers.map((answer, index) => (
                        <Typography
                            key={index}
                            variant="body-s"
                            textColor="base.subtle"
                            textAlign="start"
                            display="block"
                        >
                            {answer.answer}
                        </Typography>
                    ))}
                </Box>
                <Typography variant="body-s" textColor="base.subtle" textAlign="start" display="block" mt="16">
                    {howMuchDoesAgodaCharge.moreDetails}
                    <TextLink
                        label={howMuchDoesAgodaCharge.clickHere}
                        href={howMuchDoesAgodaCharge.link}
                        target="_blank"
                        rel="noopener"
                        hierarchy="primary"
                    />
                </Typography>
            </Accordion>
            <Divider direction="horizontal" />
            <Accordion
                actionType="chevron-icon"
                actionPosition="end-below"
                view="collapsed"
                contentSummary={
                    <Typography variant="body-default" py="16" textAlign="start">
                        {whatSolutionsDoesAgodaProvide.question}
                    </Typography>
                }
                backgroundColor="base.transparent"
                borderRadius="4"
                level={2}
                data-element-name="faq"
                data-faq-element={4}
            >
                <Box gap="16" display="flex" flexWrap="wrap">
                    <Box>
                        <Typography variant="body-s" textColor="base.subtle" textAlign="start">
                            {whatSolutionsDoesAgodaProvide.agodaOffersMultipleSolutions}
                        </Typography>
                    </Box>
                    <ul style={{ marginBottom: 0, marginTop: 0 }}>
                        {whatSolutionsDoesAgodaProvide.bulletPoints.map((point, index) => (
                            <li key={index}>
                                <Box>
                                    <Typography variant="body-s" textColor="base.subtle" display="inline">
                                        {point.bulletPoint}
                                    </Typography>
                                </Box>
                            </li>
                        ))}
                    </ul>
                    <Typography variant="body-s" textColor="base.subtle" textAlign="start" display="inline">
                        {whatSolutionsDoesAgodaProvide.moreDetails}
                        <TextLink
                            label={whatSolutionsDoesAgodaProvide.clickHere}
                            href={whatSolutionsDoesAgodaProvide.link}
                            target="_blank"
                            rel="noopener"
                            hierarchy="primary"
                        />
                    </Typography>
                </Box>
            </Accordion>
            <Divider direction="horizontal" />
        </PartnerMarketingLayout>
    );
};
